import { z } from 'zod';

import { CreationMetadata, UnityMetadata } from '@@api/services/metadata/schemas';
import { CompareOperationEnum } from '@@containers/Compare/commons/types';
import { ELEMENT_TYPES, UNITY_ELEMENT_TYPES } from '@@plate/serialization/unity/constants';
import { type LeadElement } from '@@plate/serialization/unity/elements/Lead';
import { type TitleElement } from '@@plate/serialization/unity/elements/Title';
import { type TitleHeaderElement } from '@@plate/serialization/unity/elements/TitleHeader';
import { RootPlateElement } from '@@plate/serialization/unity/groups';

import {
    ContentOwner,
    ContentStatus,
    Creator,
    Entity,
    PublicationStatus,
    RichText,
    UnityRichText,
    UnityRichTextElement,
} from '../../../utils/schemas/schemas';
import { variantsShape } from '../../../utils/schemas/utils';

const ExternalSystem = z.enum([
    'cs',
    'nn',
    'tw',
    'bw',
    'bl',
    'ls',
    'dn',
    'fw',
    'ti',
    'twa',
    'dv-gz',
    'dv-tb',
]);

const ArticleBase = Entity.extend({
    id: z.string().uuid(),
    metadataId: z.number(),
    externalId: z.string().nullish(),
    externalSystem: ExternalSystem.nullish(),
    contentOwner: ContentOwner.nullish(),
    auroraId: z.string().optional(),
    actualTextLength: z.number().nullish(),
});

export const UnityArticle = ArticleBase.extend({
    content: UnityRichText,
});

export type UnityArticle = z.infer<typeof UnityArticle>;

const ArticleVariants = variantsShape({
    headings: z.tuple([
        z.custom<TitleHeaderElement>(),
        z.custom<TitleElement>(),
        z.custom<LeadElement>(),
    ]),
});

export const Article = ArticleBase.extend({
    content: RichText,
    variants: ArticleVariants,
});

export type Article = z.infer<typeof Article>;

const TemplateElement = z.object({
    type: z.union([z.nativeEnum(ELEMENT_TYPES), z.nativeEnum(UNITY_ELEMENT_TYPES)]),
    attributes: z.record(z.string(), z.string()).optional(),
});

type TemplateElement = z.infer<typeof TemplateElement>;

export const ArticleTemplate = z.object({
    name: z.union([
        z.literal('standard'),
        z.literal('opinion'),
        z.literal('interview'),
        z.literal('listicle'),
        z.literal('factcheck'),
        z.literal('reviews'),
        z.literal('empty'),
        z.literal('reach'),
    ]),
    order: z.number(),
    elementTypes: z.array(TemplateElement),
});

export type ArticleTemplate = z.infer<typeof ArticleTemplate>;

export const NewArticle = z.object({
    template: ArticleTemplate.shape.name,
    metadata: CreationMetadata,
});
export type NewArticle = z.infer<typeof NewArticle>;

const ContentForm = z.union([z.literal('qa'), z.literal('short')]).nullable();

export const LiquifyFormat = z.object({
    contentForm: ContentForm,
    tenantIds: z.array(z.number()),
});

export type LiquifyFormat = z.infer<typeof LiquifyFormat>;

export const Liquify = z.object({
    formats: z.array(LiquifyFormat),
});

export type Liquify = z.infer<typeof Liquify>;

export const LiquifiedArticle = z.object({
    id: z.string(),
    liquidForm: ContentForm,
    metadata: UnityMetadata.pick({
        id: true,
        teasers: true,
        tenantIds: true,
        publishedAt: true,
        updatedAt: true,
    }),
});

export type LiquifiedArticle = z.infer<typeof LiquifiedArticle>;

const RevisionDataBase = Entity.extend({
    id: z.string(),
    contentOwner: ContentOwner.nullish(),
    externalId: z.string().nullable(),
    externalSystem: ExternalSystem.nullable(),
    metadataId: z.number(),
    sourceArticleId: z.string().nullable(),
});

const UnityRevisionData = RevisionDataBase.extend({
    content: UnityRichText,
});

const RevisionData = RevisionDataBase.extend({
    content: RichText,
    variants: ArticleVariants,
});

export type RevisionData = z.infer<typeof RevisionData>;

export const Revision = Entity.extend({
    creator: Creator,
    date: z.string(),
    id: z.string(),
    resourceId: z.string(),
    resourceName: z.string(),
    revisionNumber: z.number(),
    contentStatus: ContentStatus,
    publicationStatus: PublicationStatus,
});

export type Revision = z.infer<typeof Revision>;

export const DetailedUnityRevision = Revision.extend({
    revisionData: UnityRevisionData,
});

export type DetailedUnityRevision = z.infer<typeof DetailedUnityRevision>;

const DetailedRevision = Revision.extend({
    revisionData: RevisionData,
});

export type DetailedRevision = z.infer<typeof DetailedRevision>;

// @ts-expect-error TS7006 Issue #4120
export const isArticle = (entity): entity is Article => 'content' in entity;

const AddOperationBase = z.object({
    operation: z.literal(CompareOperationEnum.ADD),
});
const UnityAddOperation = AddOperationBase.extend({
    element: UnityRichTextElement,
});

const AddOperation = AddOperationBase.extend({
    element: RootPlateElement,
});

const IdentityOperation = z.object({
    operation: z.literal(CompareOperationEnum.IDENTITY),
});

export type IdentityOperation = z.infer<typeof IdentityOperation>;

const UnityUpdateOperation = z.object({
    operation: z.literal(CompareOperationEnum.UPDATE),
    element: UnityRichTextElement,
});
const UpdateOperation = z.object({
    operation: z.literal(CompareOperationEnum.UPDATE),
    element: RootPlateElement,
});

const RemoveOperation = z.object({
    operation: z.literal(CompareOperationEnum.REMOVE),
});

type RemoveOperation = z.infer<typeof RemoveOperation>;

const MoveStart = z.object({
    operation: z.literal(CompareOperationEnum.MOVE_START),
    to: z.number(),
});

type MoveStart = z.infer<typeof MoveStart>;

const BaseMoveEnd = z.object({
    operation: z.literal(CompareOperationEnum.MOVE_END),
    from: z.number(),
});
const UnityUpdatedMoveEnd = BaseMoveEnd.extend({
    updated: z.literal(true),
    element: UnityRichTextElement,
});
const UpdatedMoveEnd = BaseMoveEnd.extend({
    updated: z.literal(true),
    element: RootPlateElement,
});
const UnityMoveEnd = z.union([BaseMoveEnd, UnityUpdatedMoveEnd]);
const MoveEnd = z.union([BaseMoveEnd, UpdatedMoveEnd]);

type MoveEnd = z.infer<typeof MoveEnd>;
const UnityMoveOperation = z.union([MoveStart, UnityMoveEnd]);
const MoveOperation = z.union([MoveStart, MoveEnd]);
const UnityActionOperation = z.union([UnityAddOperation, UnityUpdateOperation]);
const ActionOperation = z.union([AddOperation, UpdateOperation]);

export type ActionOperation = z.infer<typeof ActionOperation>;

const UnityCompareOperation = z.union([
    IdentityOperation,
    RemoveOperation,
    UnityActionOperation,
    UnityMoveOperation,
]);

const CompareOperation = z.union([
    IdentityOperation,
    RemoveOperation,
    ActionOperation,
    MoveOperation,
]);

const DerivativeArticleBase = z.object({
    revisionSource: z.string(),
    revision: z.string().nullable(),
});

const OriginArticleBase = z.object({
    revisionSource: z.string(),
    revision: z.string().nullable(),
});

export const UnityCompare = z.object({
    derivativeArticle: DerivativeArticleBase.extend({
        contentModifications: z.array(UnityCompareOperation),
    }),
    originArticle: OriginArticleBase.extend({
        content: z.object({
            richTextFormat: z.array(z.union([UnityRichTextElement, z.null()])),
        }),
    }),
});
export type UnityCompare = z.infer<typeof UnityCompare>;

export const Compare = z.object({
    derivativeArticle: DerivativeArticleBase.extend({
        contentModifications: z.array(CompareOperation),
    }),
    originArticle: OriginArticleBase.extend({
        content: z.array(z.union([RootPlateElement, z.null()])),
    }),
});
export type Compare = z.infer<typeof Compare>;
